/**
 * Admin "Système" pane.
 *
 * Surfaces operational health signals so an admin can answer at a glance:
 *  - When did we last ship the frontend / Supabase functions?
 *  - When did the last cron run finish?
 *  - How many errors have piled up in the last 24 h / 7 days?
 *  - Have any Plesk provisionings failed recently?
 *
 * Reads `public.get_admin_system_health()` (Batch 10 migration) which
 * does the admin gate server-side via is_admin().
 */

import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, CheckCircle2, Clock, Server, Upload, Workflow } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface SystemHealth {
  last_frontend_deploy_at: string | null;
  last_supabase_deploy_at: string | null;
  supabase_region: string | null;
  last_cron_run_at: string | null;
  error_events_last_7d: number;
  error_events_last_24h: number;
  provisioning_failures_last_7d: number;
}

function formatTimestamp(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
}

function formatAge(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const ageMs = Date.now() - date.getTime();
  const hours = Math.floor(ageMs / 3_600_000);
  if (hours < 1) return "il y a moins d'une heure";
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}

export default function AdminSystemPage() {
  useEffect(() => {
    document.title = "Système — Delapresse";
    return () => {
      document.title = "Delapresse";
    };
  }, []);

  const { data, isLoading, error, refetch, isFetching } = useQuery<SystemHealth | null>({
    queryKey: ["admin-system-health"],
    queryFn: async () => {
      // get_admin_system_health() returns a single row; supabase-js
      // exposes RPC return values directly as `data`.
      const { data, error } = await supabase.rpc("get_admin_system_health");
      if (error) throw error;
      // RPC of a returns-row function may surface as a single object
      // or a one-element array depending on schema; normalise both.
      if (Array.isArray(data)) return (data[0] as SystemHealth) ?? null;
      return (data as SystemHealth) ?? null;
    },
    refetchInterval: 60_000,
  });

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-border bg-background/80 px-6 backdrop-blur-xl">
        <div>
          <h1 className="text-lg font-semibold">Système</h1>
          <p className="text-xs text-muted-foreground">Santé opérationnelle, dernières opérations.</p>
        </div>
        <button
          onClick={() => refetch()}
          className="text-xs text-primary hover:underline disabled:opacity-50"
          disabled={isFetching}
        >
          {isFetching ? "Actualisation…" : "Actualiser"}
        </button>
      </header>

      <main className="mx-auto max-w-5xl space-y-6 p-6">
        {error ? (
          <Card>
            <CardContent className="p-6 text-sm text-destructive">
              <AlertCircle className="mr-2 inline h-4 w-4" />
              {error instanceof Error ? error.message : "Impossible de charger l'état du système."}
            </CardContent>
          </Card>
        ) : isLoading || !data ? (
          <p className="text-sm text-muted-foreground">Chargement…</p>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-3">
              <StatCard
                icon={<AlertCircle className="h-4 w-4" />}
                label="Erreurs (24 h)"
                value={data.error_events_last_24h.toString()}
                badge={data.error_events_last_24h === 0 ? "ok" : data.error_events_last_24h < 5 ? "warn" : "bad"}
              />
              <StatCard
                icon={<AlertCircle className="h-4 w-4" />}
                label="Erreurs (7 j)"
                value={data.error_events_last_7d.toString()}
                badge={data.error_events_last_7d === 0 ? "ok" : data.error_events_last_7d < 20 ? "warn" : "bad"}
              />
              <StatCard
                icon={<Workflow className="h-4 w-4" />}
                label="Provisions Plesk en échec (7 j)"
                value={data.provisioning_failures_last_7d.toString()}
                badge={data.provisioning_failures_last_7d === 0 ? "ok" : "bad"}
              />
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Upload className="h-4 w-4" />
                  Dernier déploiement
                </CardTitle>
                <CardDescription>
                  Écrit par <code>scripts/deploy-plesk.ts</code> et <code>scripts/deploy-supabase.ts</code> à chaque ship.
                </CardDescription>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm md:grid-cols-2">
                <DataRow label="Frontend (Plesk)" value={data.last_frontend_deploy_at} />
                <DataRow label="Edge functions / DB" value={data.last_supabase_deploy_at} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Clock className="h-4 w-4" />
                  Dernière exécution cron
                </CardTitle>
                <CardDescription>
                  Latest <code>automation_logs</code> row. Tous les jobs pg_cron (retention, auto-fetch-news, autopublish-engine) écrivent ici.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-sm">
                <DataRow label="Dernière exécution" value={data.last_cron_run_at} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Server className="h-4 w-4" />
                  Infrastructure
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 text-sm md:grid-cols-2">
                <DataRow label="Région Supabase" value={data.supabase_region} formatter={(v) => v ?? "—"} />
                <DataRow label="Politique RGPD" value="docs/PRIVACY/ROPA.md" formatter={(v) => v ?? "—"} />
              </CardContent>
            </Card>
          </>
        )}
      </main>
    </div>
  );
}

function StatCard({
  icon,
  label,
  value,
  badge,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  badge: "ok" | "warn" | "bad";
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
          {badge === "ok" && (
            <Badge variant="outline" className="border-green-500/40 text-green-700 dark:text-green-400">
              <CheckCircle2 className="mr-1 h-3 w-3" />
              OK
            </Badge>
          )}
          {badge === "warn" && (
            <Badge variant="outline" className="border-amber-500/40 text-amber-700 dark:text-amber-400">
              {icon}
            </Badge>
          )}
          {badge === "bad" && (
            <Badge variant="outline" className="border-destructive/40 text-destructive">
              {icon}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-3xl font-bold">{value}</p>
      </CardContent>
    </Card>
  );
}

function DataRow({
  label,
  value,
  formatter,
}: {
  label: string;
  value: string | null;
  formatter?: (value: string | null) => string;
}) {
  const formatted = formatter ? formatter(value) : formatTimestamp(value);
  const subtitle = value ? formatAge(value) : null;
  return (
    <div className="rounded-md border border-border bg-secondary/30 p-3">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-medium">{formatted}</p>
      {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
    </div>
  );
}
